import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import CookieConsent from "@/components/CookieConsent";

import Home from "@/pages/Home";
import Solutions from "@/pages/Solutions";
import DemoCatalogue from "@/pages/DemoCatalogue";
import DemoDetail from "@/pages/DemoDetail";
import GuidedJourney from "@/pages/GuidedJourney";
import HowItWorks from "@/pages/HowItWorks";
import Consultation from "@/pages/Consultation";
import Contact from "@/pages/Contact";
import PopupPrep from "@/pages/demos/PopupPrep";
import OpenHouseTracker from "@/pages/demos/OpenHouseTracker";
import StampCard from "@/pages/demos/StampCard";
import RoundRunner from "@/pages/demos/RoundRunner";
import VoucherVault from "@/pages/demos/VoucherVault";
import ChapterOne from "@/pages/demos/ChapterOne";
import WalkClub from "@/pages/demos/WalkClub";
import SwapCircle from "@/pages/demos/SwapCircle";
import AllotmentRota from "@/pages/demos/AllotmentRota";
import OrderJar from "@/pages/demos/OrderJar";
import QuietRoom from "@/pages/demos/QuietRoom";
import HeroPreview from "@/pages/HeroPreview";
import FeaturesPreview from "@/pages/FeaturesPreview";
import LegalPage from "@/pages/LegalPage";
import CoInnovation from "@/pages/CoInnovation";
import PromoCode from "@/pages/PromoCode";
import EventCard from "@/pages/EventCard";
import EventLanding from "@/pages/EventLanding";
import DigitalCard from "@/pages/DigitalCard";
import IdeaLab from "@/pages/IdeaLab";
import IdeaLabWizard from "@/pages/IdeaLabWizard";
import IdeaLabSubmitted from "@/pages/IdeaLabSubmitted";
import ScrollToTop from "@/components/ScrollToTop";

import AdminLogin from "@/pages/admin/Login";
import AuthCallback from "@/pages/admin/AuthCallback";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminLeads from "@/pages/admin/Leads";
import AdminLeadDetail from "@/pages/admin/LeadDetail";
import AdminConsultations from "@/pages/admin/Consultations";
import AdminDemos from "@/pages/admin/Demos";
import AdminPromotions from "@/pages/admin/Promotions";
import AdminEventCodes from "@/pages/admin/EventCodes";
import AdminUsers from "@/pages/admin/Users";
import AdminAudit from "@/pages/admin/AuditLog";
import AdminReports from "@/pages/admin/Reports";
import AdminSiteContent from "@/pages/admin/SiteContent";
import AdminIdeaLab from "@/pages/admin/IdeaLabAdmin";
import AdminIdeaLabDetail from "@/pages/admin/IdeaLabAdminDetail";
import AdminIdeaLabBrief from "@/pages/admin/IdeaLabAdminBrief";

import ProtectedRoute from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";
import PublicLayout from "@/components/PublicLayout";
import { SiteSettingsProvider } from "@/context/SiteSettingsContext";
import AdminFeatures from "@/pages/admin/Features";

function RouterShell() {
  const location = useLocation();
  // Emergent OAuth returns with session_id in URL fragment — handle FIRST
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/demos" element={<DemoCatalogue />} />
        <Route path="/demos/:slug" element={<DemoDetail />} />
        <Route path="/guided-journey" element={<GuidedJourney />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/consultation" element={<Consultation />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<LegalPage slug="privacy" />} />
        <Route path="/terms" element={<LegalPage slug="terms" />} />
        <Route path="/co-innovation" element={<CoInnovation />} />
        <Route path="/idealab" element={<IdeaLab />} />
      </Route>

      {/* IdeaLab wizard + confirmation (no public header/footer for app-like feel) */}
      <Route path="/idealab/start" element={<IdeaLabWizard />} />
      <Route path="/idealab/submitted/:ref" element={<IdeaLabSubmitted />} />

      {/* Magic short URLs that unlock the gated promo + full event-special landing page */}
      <Route element={<PublicLayout />}>
        <Route path="/event/:code" element={<EventLanding />} />
      </Route>
      <Route path="/event" element={<Navigate to="/event/hl1may" replace />} />
      <Route path="/c/:code" element={<PromoCode />} />

      {/* Printable event handout (no header/footer) */}
      <Route path="/print/event-card" element={<EventCard />} />
      <Route path="/print/event-card/:variant" element={<EventCard />} />

      {/* Digital business card — opens via QR or NFC, sets promo flag */}
      <Route path="/card" element={<DigitalCard />} />

      {/* Standalone concept-demo sandboxes (no public header/footer for app-like feel) */}
      {/* /demos/holborough-social/try retired — flagship has a live app; redirect stragglers to the case study */}
      <Route path="/demos/holborough-social/try" element={<Navigate to="/demos/holborough-social" replace />} />
      <Route path="/demos/popup-prep/try" element={<PopupPrep />} />
      <Route path="/demos/open-house-tracker/try" element={<OpenHouseTracker />} />
      <Route path="/demos/stamp-card/try" element={<StampCard />} />
      <Route path="/demos/round-runner/try" element={<RoundRunner />} />
      <Route path="/demos/voucher-vault/try" element={<VoucherVault />} />
      <Route path="/demos/chapter-one/try" element={<ChapterOne />} />
      <Route path="/demos/walk-club/try" element={<WalkClub />} />
      <Route path="/demos/swapcircle/try" element={<SwapCircle />} />
      <Route path="/demos/allotment-rota/try" element={<AllotmentRota />} />
      <Route path="/demos/orderjar/try" element={<OrderJar />} />
      <Route path="/demos/quietroom/try" element={<QuietRoom />} />
      <Route path="/hero-previews" element={<HeroPreview />} />
      <Route path="/features-preview/holborough" element={<FeaturesPreview />} />

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/auth/callback" element={<AuthCallback />} />

      {/* Print-only brief: needs auth but rendered OUTSIDE AdminLayout so the print view is chrome-free */}
      <Route
        path="/admin/idealab/:ref/brief"
        element={
          <ProtectedRoute>
            <AdminIdeaLabBrief />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="leads/:id" element={<AdminLeadDetail />} />
        <Route path="consultations" element={<AdminConsultations />} />
        <Route path="demos" element={<AdminDemos />} />
        <Route path="promotions" element={<AdminPromotions />} />
        <Route path="event-codes" element={<AdminEventCodes />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="content" element={<AdminSiteContent />} />
        <Route path="features" element={<AdminFeatures />} />
        <Route path="idealab" element={<AdminIdeaLab />} />
        <Route path="idealab/:ref" element={<AdminIdeaLabDetail />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App" data-testid="app-root">
      <AuthProvider>
        <SiteSettingsProvider>
          <BrowserRouter>
            <ScrollToTop />
            <RouterShell />
            <Toaster position="top-right" richColors closeButton />
            <CookieConsent />
          </BrowserRouter>
        </SiteSettingsProvider>
      </AuthProvider>
    </div>
  );
}
